import React, { useState, useEffect } from "react"
import axios from "axios"
import { useNavigate, useParams, Link } from "react-router-dom"
import { FaAngleLeft, FaPlus } from 'react-icons/fa'
import Alert from 'react-bootstrap/Alert'
import Form from 'react-bootstrap/Form'
import { apiURL, filesURL, sitePath } from 'config/globals'
import Topbar from 'components/Topbar'
import CraftRecordNavigation from 'components/CraftRecordNavigation'
import FormRow from 'components/FormRow'
import CancelButton from 'components/CancelButton'
import SaveStepButton from 'components/SaveStepButton'
import Footer from 'components/Footer'
import { checkAuth } from 'utils/Auth'
import ReactImagePickerEditor from 'react-image-picker-editor'
import 'react-image-picker-editor/dist/index.css'

const craftTypeOptions = [
    { value: 'traditional', label: 'Traditional' },
    { value: 'historical', label: 'Historical' },
    { value: 'experimental', label: 'Experimental' },
]

const CraftRecord1 = () => {
    const [currentUser, setCurrentUser] = useState()
    const [museum, setMuseum] = useState({})
    const [craftRecord, setCraftRecord] = useState({
        type: 'traditional'
    })
    const [form, setForm] = useState()
    const [loadError, setLoadError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState('')

    const navigate = useNavigate()
    const { entityId, craftRecordId } = useParams()
    
    useEffect(() => {
        const start = async () => {
            setCurrentUser(await checkAuth(navigate))
            await getMuseum(entityId)
            if (craftRecordId) {
                await getCraftRecord(craftRecordId)
            }
        }
        start()
    }, [])

    const getMuseum = async (id) => {
        try {
            const response = await axios.get(`${apiURL}/get-museum/${id}`)

            if (!response.data.success) {
                setLoadError("Cannot load museum data: " + (response.data.error || response.data))
                return
            }

            setMuseum(response.data.item)
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const getCraftRecord = async (id) => {
        try {
            const response = await axios.get(`${apiURL}/get-craft-record/${id}`)

            if (!response.data.success) {
                setLoadError("Cannot load record data: " + (response.data.error || response.data))
                return
            }

            setCraftRecord(response.data.item)
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const handleChange = (field, value) => {
        setForm({
            ...form,
            [field]: value
        })
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        event.stopPropagation()

        setSubmitError('')
        setIsSubmitting(true)

        const submittedForm = {...form}

        if (!craftRecord.id) {
            submittedForm.museumId = entityId
            submittedForm.createdBy = currentUser.email
        }
        
        try {
            const response = craftRecord.id
                ? await axios.put(`${apiURL}/update-craft-record/${craftRecord.id}`, submittedForm)
                : await axios.post(`${apiURL}/create-craft-record`, submittedForm)
            
            if (!response.data.success) {
                setSubmitError(response.data.error || response.data)
                setIsSubmitting(false)
                return
            }

            setIsSubmitting(false)
            navigate(`/museum/${museum.id}/documentation/craft-records/${craftRecord.id ? craftRecord.id : response.data.id}/edit-video`)
        } catch (error) {
            setSubmitError(JSON.stringify(error))
            setIsSubmitting(false)
        }
    }

    const cancel = () => {
        navigate(`/museum/${museum.id}/documentation/craft-records`)
    }

    return (
        <>
            <Topbar currentUser={currentUser} />
            <main className="full-width">
                {museum.id &&
                    <>
                        <div>
                            <Link to={`/museum/${museum.id}/documentation/craft-records`} className="no-decoration"><FaAngleLeft /> Back to Craft Records</Link>
                        </div>
                        <header className="mt-2">
                            <div className="float-start">
                                <h1>Craft Record</h1>
                            </div>
                        </header>
                        <CraftRecordNavigation currentTab="summary" museumId={museum.id} craftRecordId={craftRecord.id} />
                        {(!craftRecordId || craftRecord.id) &&
                            <>
                                <FormRow
                                    key="craftName"
                                    label="Name of craft"
                                    required={true}
                                    content={
                                        <Form.Control
                                            name="name"
                                            placeholder="Write a name for the craft activity"
                                            defaultValue={craftRecord.name}
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                        />
                                    }
                                />
                                <FormRow
                                    key="type"
                                    label="Type of craft"
                                    required={true}
                                    content={
                                        <Form.Select
                                            name="craftType"
                                            defaultValue={craftRecord.craftType}
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                        >
                                            {craftTypeOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))
                                            }
                                        </Form.Select>
                                    }
                                />
                                <FormRow
                                    key="shortDescription"
                                    label="Short description"
                                    hint={<>Max 1000 characters</>}
                                    required={true}
                                    content={
                                        <Form.Control
                                            name="shortDescription"
                                            placeholder="Write a short description of the craft activity"
                                            defaultValue={craftRecord.shortDescription}
                                            as="textarea"
                                            rows={10}
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                        />
                                    }
                                />
                                <FormRow
                                    key="inventoryNumber"
                                    label="Inventory number"
                                    content={
                                        <Form.Control
                                            name="inventoryNumber"
                                            defaultValue={craftRecord.inventoryNumber}
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                        />
                                    }
                                />
                                <FormRow
                                    key="mainImage"
                                    label="Cover Image"
                                    hint={<>Accepted types: JPG or PNG<br/>Recommended size: 1200x800</>}
                                    required={true}
                                    content={
                                        <div className="mb-3">
                                            <ReactImagePickerEditor
                                                config={{
                                                    borderRadius: '8px',
                                                    language: 'en',
                                                    width: '330px',
                                                    height: '250px',
                                                    objectFit: 'contain',
                                                    compressInitial: null,
                                                }}
                                                imageSrcProp={craftRecord.mainImage
                                                    ? `${filesURL}/${craftRecord.mainImage}`
                                                    : null
                                                }
                                                imageChanged={(dataUri) => {
                                                    handleChange('mainImage', {
                                                        uri: dataUri
                                                    })
                                                }}
                                            />
                                        </div>
                                    }
                                />
                                <FormRow
                                    key="published"
                                    label="Published"
                                    content={
                                        <Form.Check 
                                            type="checkbox"
                                            name="published"
                                            className="mt-1"
                                            defaultChecked={parseInt(craftRecord.published) ? "checked" : ""}
                                            onChange={e => handleChange('published', e.target.checked ? 1 : 0)}
                                        />
                                    }
                                />
                                {submitError && 
                                    <Alert key='submitError' variant='danger'>
                                        {submitError}
                                    </Alert>
                                }
                                <div className="mt-5 mb-4 d-grid d-lg-block">
                                    <SaveStepButton isSubmitting={isSubmitting} onClick={handleSubmit} />
                                    <CancelButton onClick={cancel} />
                                </div>
                            </>
                        }
                    </>
                }
                {loadError &&
                    <>
                        <Alert key='loadError' variant='danger'>
                            {loadError}
                        </Alert>
                    </>
                }
                <Footer user={currentUser} />
            </main>
        </>
    )
}

export default CraftRecord1